/**
 * The asset-class registry — Steward's canonical, declarative model of what an
 * asset class IS and what a record of that class carries.
 *
 * WHY THIS LIVES HERE (friction log F5). Asset class is a first-class product
 * concept, not a form detail. Before this file the knowledge was spread across at
 * least seven private lists that each knew a slice of it and none of which knew
 * about the others: `EQUITY_CLASSES` / `RE_CLASSES` / `TICKERED_CLASSES` in the
 * SPA's portfolio forms, `assetGroupFor`, `MARKET_PRICED_CLASSES`, the façade's own
 * `RE_CLASSES`, the snapshot calculator's buckets, the price enricher, and each
 * market-price source's `supportedAssetClasses`. Adding real-estate address fields
 * would have meant an eighth. `@steward-app/steward-types` is already the shared
 * source consumed by the SPA, the façade API, the background services and the App
 * Pack seeded into appcore, so it is where a class registry has to live for all of
 * them to agree.
 *
 * THE CODE / LABEL SPLIT is the point (F19). Until now the display label WAS the
 * stored value: the picker wrote `'Equity'`, Plaid's mapper wrote `'equity'`, and
 * renaming a label meant rewriting a pipeline. Here a class has a **stable lowercase
 * `code`** that is what gets stored, a **`label`** that is only ever displayed, and
 * **`synonyms`** for values already in the table. So "Equity" could become "Stock"
 * as a one-word edit, and `'Equity'`, `'equity'` and `'stock'` all still resolve.
 *
 * STORED VALUES ARE NEVER MIGRATED (balance-sheet plan D7). Everything here resolves
 * case-insensitively and degrades gracefully: an unrecognised stored class keeps its
 * own spelling, lands in the `Other` group, and stays selectable in the picker rather
 * than being silently rewritten to the first entry.
 *
 * FIELD ORDER IS PART OF THE DESCRIPTOR, not an afterthought (F7). For a tickered
 * class the ticker must precede the name, because the ticker auto-fills the name and
 * a user who types the name first permanently suppresses that. The right order for a
 * stock is not the right order for a property, so `fieldOrder` is per class and the
 * forms render from it rather than from hand-placed JSX.
 */

// ── Reporting groups ──────────────────────────────────────────────────────────

/**
 * The rollup a class reports under. Class is specific, group is the rollup: an ETF
 * stays an ETF while reporting alongside stocks, so keeping the two distinct costs
 * nothing in the summary views (F19).
 *
 * The order is roughly most-liquid to least, and every summary surface (dashboard
 * allocation, Portfolio grouping, Assets by-class view) iterates this array rather than
 * naming groups, so adding a group here is the whole change for those surfaces.
 * `Business` sits directly after `Private Equity` (steward-app#1195): both are illiquid
 * direct ownership, but a family's own operating company is not "private equity" to the
 * owner-operator who runs it, so it gets a group of its own. The group is what the
 * summary surfaces render; it is not a guaranteed dashboard line. The dashboard's
 * allocation (`buildAllocation` in `src/pages/Dashboard.tsx`) shows the top `ALLOC_MAX`
 * groups by value (6 at the time of writing) and folds the remainder into `Other`, so a
 * business appears as itself when it ranks and as `Other` when it does not. That fold is
 * deliberate and applies to every group equally; do not special-case a group past it.
 * The snapshot calculator only ever looks
 * up `Equities`, `Real Estate` and `Cash` by name and treats everything else as a
 * residual, which is why a new group cannot move a stored total; the calculator test
 * named for #1027 pins that.
 */
export const ASSET_GROUP_ORDER = [
  'Equities', 'Options', 'Fixed Income', 'Real Estate', 'Cash',
  'Private Equity', 'Business', 'Precious Metals', 'Collectibles', 'Vehicles', 'Insurance',
  'Loans Receivable', 'Alternative', 'Crypto', 'Other',
] as const;

export type AssetGroup = (typeof ASSET_GROUP_ORDER)[number];

// ── Field descriptors ─────────────────────────────────────────────────────────

/**
 * How a field is captured. These are Steward widgets, not HTML input types: several
 * are bespoke controls (a ticker typeahead, an entity picker) whose behaviour is the
 * reason the field exists at all.
 */
export type AssetFieldWidget =
  | 'text'
  /**
   * A calendar date the family picks, stored as a bare `YYYY-MM-DD` string. Distinct
   * from `text` because the renderer has real work to do here: `<input type="date">`
   * renders BLANK for any value it cannot parse, so a widget name alone is not enough
   * and the renderer has to fall back for a stored value that is not a real date.
   *
   * This is a capture change only. `storedType` stays `string` and the CTYPE keeps
   * declaring the attribute as a string, because `YYYY-MM-DD` is already what the
   * picker reads and writes; nothing about how the value is stored moves.
   */
  | 'date'
  | 'ticker'
  | 'currency'
  | 'account'
  | 'entity'
  | 'valuation-mode'
  | 'select'
  | 'number'
  | 'address';

export interface AssetFieldSpec {
  /** Attribute name on the stored asset — must match the App Pack CTYPE field. */
  readonly name: string;
  readonly label: string;
  readonly widget: AssetFieldWidget;
  /** Required for every class that includes the field. */
  readonly required?: boolean;
  /**
   * Required only when a sibling field holds a particular value. Used for the
   * address parts, which are required for an automated valuation (the AVM needs a
   * resolvable address) but optional for a property the family values themselves.
   */
  readonly requiredWhen?: { readonly field: string; readonly equals: string };
  readonly placeholder?: string;
  /** Short help shown under the input. */
  readonly help?: string;
  /** Occupies the whole form row rather than one column of the pair. */
  readonly fullWidth?: boolean;
  /**
   * Value for the input's `autocomplete` attribute (F6). Set on the address parts so
   * that a user who ignores or cannot use our own suggestions still gets the browser's
   * native address autofill, which is the fastest path for a family's own home.
   * `addressLine1` deliberately opts OUT, because the native sheet would render on top
   * of our suggestion list and leave two stacked dropdowns.
   */
  readonly autoComplete?: string;
  /**
   * Choices for a `select` widget, declared by the registry rather than hardcoded in
   * the renderer. `assetClass` predates this and still builds its own options from
   * `ASSET_CLASSES`, because its list is the class registry itself; any other select
   * states its options here.
   */
  readonly options?: readonly AssetFieldOption[];
  /**
   * The type the App Pack CTYPE declares for this attribute. Defaults to `string`.
   *
   * Stated here rather than inferred from the widget, because the CTYPE spread in
   * `index.ts` reads this registry and would otherwise have to re-derive it — a second
   * place to get it wrong, on a field whose whole purpose is that both sides agree.
   * A `number` field is stored as a NUMBER, not a numeric string: it is summed and
   * compared downstream, and `"1556" < "900"` is true.
   */
  readonly storedType?: 'string' | 'number';
  /** Suffix rendered after the input, e.g. "sq ft". Purely presentational. */
  readonly unit?: string;
  /**
   * The `number` widget holds an amount of money, so the SPA formats it as the workspace
   * currency when the input loses focus (steward-app#1979). Presentational only: the stored
   * value is still a plain number and `storedType` still governs the CTYPE. Opt-in rather
   * than inferred, because the same widget draws counts and years (bedrooms, year built)
   * that must never gain a currency symbol.
   */
  readonly money?: boolean;
}

export interface AssetFieldOption {
  readonly value: string;
  readonly label: string;
}

/**
 * The structured property address (F5). Replaces the single free-text `address`
 * string outright: that string was modelled as an INPUT TO THE AVM rather than as an
 * attribute of the property, which is why it only ever rendered in automated-estimate
 * mode and was never displayed anywhere.
 *
 * Flat, prefixed attributes rather than a nested object: appcore CTYPE fields are a
 * flat name/type list, so flat keys are what the platform can actually validate.
 */
export const ASSET_ADDRESS_FIELDS: readonly AssetFieldSpec[] = [
  {
    // The one address field with a picker on it (F6): the user types a street address,
    // picks a match, and the remaining parts below are filled from the picked result.
    name: 'addressLine1', label: 'Street address', widget: 'address',
    placeholder: '123 Main St', fullWidth: true,
    help: 'Start typing and pick a match, or type the full address yourself.',
    requiredWhen: { field: 'valuationMode', equals: 'avm' },
  },
  {
    // Never filled by a pick: the provider has no opinion about a unit number, and it
    // is the one part where what the user typed is the authority.
    name: 'addressLine2', label: 'Apartment, suite, unit', widget: 'text',
    placeholder: 'Unit 4B', autoComplete: 'address-line2',
  },
  {
    name: 'addressCity', label: 'City', widget: 'text',
    placeholder: 'San Francisco', autoComplete: 'address-level2',
    requiredWhen: { field: 'valuationMode', equals: 'avm' },
  },
  {
    name: 'addressState', label: 'State or region', widget: 'text',
    placeholder: 'CA', autoComplete: 'address-level1',
    requiredWhen: { field: 'valuationMode', equals: 'avm' },
  },
  {
    name: 'addressPostalCode', label: 'Postal code', widget: 'text',
    placeholder: '94115', autoComplete: 'postal-code',
    requiredWhen: { field: 'valuationMode', equals: 'avm' },
  },
  {
    name: 'addressCountry', label: 'Country', widget: 'text',
    placeholder: 'United States', autoComplete: 'country-name',
  },
];

export const ASSET_ADDRESS_FIELD_NAMES: readonly string[] =
  ASSET_ADDRESS_FIELDS.map(f => f.name);

/** The address parts as a plain shape, for callers that only handle the address. */
export interface StructuredAddress {
  addressLine1?: string;
  addressLine2?: string;
  addressCity?: string;
  addressState?: string;
  addressPostalCode?: string;
  addressCountry?: string;
}

// ── Property details ──────────────────────────────────────────────────────────

/**
 * What kind of dwelling a property is.
 *
 * Stored as OUR code, never the provider's string. RentCast wants exactly
 * `Single Family` / `Condo` / `Townhouse` / `Manufactured` / `Multi-Family` /
 * `Apartment` / `Land`, case-sensitively, and the mapping to those lives at the
 * provider boundary in the refresher's `rentcast.ts`. Storing a vendor's spelling in a
 * family's record would make the row a hostage to that vendor: `assetClass` settled the
 * same question the same way ("the stored code stays `equity` forever").
 *
 * Labels speak to a family rather than to an assessor. "Single Family" is how a
 * valuation model names a detached house; "House (detached)" is how someone describes
 * where they live.
 */
export const PROPERTY_TYPE_OPTIONS: readonly AssetFieldOption[] = [
  { value: 'single-family', label: 'House (detached)' },
  { value: 'townhouse',     label: 'Townhouse (shares a wall)' },
  { value: 'condo',         label: 'Condominium' },
  { value: 'multi-family',  label: 'Multi-family (2 to 4 units)' },
  { value: 'apartment',     label: 'Apartment building (5+ units)' },
  { value: 'manufactured',  label: 'Manufactured or mobile home' },
  { value: 'land',          label: 'Land' },
];

/**
 * Property attributes beyond the address. One field today; the group exists because
 * this is the seam the CTYPE spread and the form order both read from, exactly as
 * `ASSET_ADDRESS_FIELDS` does, so adding the rest later is one edit in one place.
 *
 * ⚠️ **Not `requiredWhen: valuationMode === 'avm'`, deliberately.** Requiring it would
 * be the stricter reading of steward-app#287, but it would also block editing every
 * property already stored without one, for a field the family has no reason to think
 * they owe us. It is optional and consulted when present; the automatic fill that makes
 * it reliably populated is the follow-up. An absent value leaves the provider to resolve
 * the type itself, which is exactly the pre-existing behaviour, so this can only improve
 * an estimate and never degrade one.
 */
export const ASSET_PROPERTY_DETAIL_FIELDS: readonly AssetFieldSpec[] = [
  {
    name: 'propertyType', label: 'Property type', widget: 'select',
    options: PROPERTY_TYPE_OPTIONS,
    help: 'Used to compare your property against similar ones. Leave it blank if you are not sure.',
  },
  { name: 'bedrooms',        label: 'Bedrooms',   widget: 'number', storedType: 'number' },
  // Fractional on purpose: half baths are ordinary and "2.5" is how a family says it.
  { name: 'bathrooms',       label: 'Bathrooms',  widget: 'number', storedType: 'number' },
  { name: 'squareFootage',   label: 'Living area', widget: 'number', storedType: 'number', unit: 'sq ft' },
  { name: 'lotSize',         label: 'Lot size',    widget: 'number', storedType: 'number', unit: 'sq ft' },
  { name: 'yearBuilt',       label: 'Year built',  widget: 'number', storedType: 'number' },
  {
    // Not a valuation. This is what the family PAID, which is a cost-basis anchor they
    // often cannot remember, and it must never be confused with what the property is
    // worth now — hence the label, and hence it living beside the sale date.
    name: 'lastSalePrice', label: 'Last sale price', widget: 'number', storedType: 'number', money: true,
    help: 'What it last sold for, from public records. Not an estimate of value today.',
  },
  {
    // A date picker, not a box asking a family to hand-type ISO 8601 (steward-app#967).
    // The public-records lookup fills this when it can, but a family whose lookup found
    // nothing was left typing the format from the placeholder, three rows above a real
    // picker on the purchase date. The `placeholder` survives for the renderer's
    // free-text fallback, which is the only branch that can still show one.
    name: 'lastSaleDate', label: 'Last sale date', widget: 'date', placeholder: 'YYYY-MM-DD',
  },
  {
    name: 'annualPropertyTax', label: 'Annual property tax', widget: 'number', storedType: 'number', money: true,
  },
];

export const ASSET_PROPERTY_DETAIL_FIELD_NAMES: readonly string[] =
  ASSET_PROPERTY_DETAIL_FIELDS.map(f => f.name);

/** Every non-address field an asset form can render, keyed by attribute name. */
const COMMON_FIELDS: readonly AssetFieldSpec[] = [
  { name: 'assetClass', label: 'Asset class', widget: 'select', required: true },
  { name: 'name', label: 'Name', widget: 'text', required: true },
  {
    name: 'ticker', label: 'Ticker', widget: 'ticker',
    placeholder: 'AAPL',
  },
  {
    // Label speaks to the family, not to the system. "Valuation source" is quiet
    // jargon, and the stored enum (`avm` / `stated`) is never shown to anyone.
    name: 'valuationMode', label: 'How this property is valued', widget: 'valuation-mode',
    fullWidth: true,
  },
  { name: 'accountId', label: 'Account', widget: 'account' },
  { name: 'currency', label: 'Currency', widget: 'currency', required: true },
  { name: 'ownerEntityId', label: 'Owner', widget: 'entity' },
];

const FIELD_BY_NAME: ReadonlyMap<string, AssetFieldSpec> = new Map(
  [...COMMON_FIELDS, ...ASSET_ADDRESS_FIELDS, ...ASSET_PROPERTY_DETAIL_FIELDS].map(f => [f.name, f]),
);

// ── Field orders ──────────────────────────────────────────────────────────────

/**
 * SECTIONS (steward-app#963). A class's field order may carry `{ section }` markers
 * between field names, and the form renders each one as a heading over the fields
 * that follow it.
 *
 * Declared HERE rather than in the renderer, and as markers inside the order rather
 * than as a parallel list, for the same reason `fieldOrder` itself lives here: the
 * grouping is a fact about what a class IS, and a second list stating which fields
 * belong to which heading would be a copy of the order that drifts from it. A section
 * cannot name a field this class does not render, because it does not name fields at
 * all — it names the boundary.
 *
 * A class that declares NO sections renders exactly as before, one flat grid. That is
 * the answer to "should a five-field stock form grow headings": headings are opt-in
 * per class, so only a form long enough to need them carries them, and no threshold
 * has to be guessed at render time.
 */
export interface AssetFieldSectionMarker {
  /** Heading text, sentence case (DESIGN.md). Never an attribute name. */
  readonly section: string;
  /**
   * One line under the heading, for a section whose fields need a word about where
   * their values came from. Rendered in the same quiet style as a field's `help`.
   */
  readonly note?: string;
}

/** An entry in a class's field order: an attribute name, or a heading before one. */
export type FieldOrderEntry = string | AssetFieldSectionMarker;

/** A run of fields under one optional heading, as the forms render them. */
export interface AssetFieldGroup {
  /** Absent on the leading run when a class opens with fields before any heading. */
  readonly title?: string;
  /** The heading's one-line note, when it declared one. */
  readonly note?: string;
  readonly fields: readonly AssetFieldSpec[];
}

function isSectionMarker(entry: FieldOrderEntry): entry is AssetFieldSectionMarker {
  return typeof entry !== 'string';
}


// Every class ends the same way: where it is held, what it is denominated in, and
// who owns it. Only the head of the form varies by class.
const TAIL = ['accountId', 'currency', 'ownerEntityId'] as const;

/** Nothing class-specific: class, then name. */
const ORDER_PLAIN: readonly string[] = ['assetClass', 'name', ...TAIL];

/**
 * TICKER BEFORE NAME, deliberately (F7). The ticker typeahead auto-fills the name,
 * but the fill is suppressed once the user has touched the name field — so meeting a
 * required-looking Name first is what defeated the feature. Leading with the ticker
 * makes Name read as the auto-filled result it is.
 */
const ORDER_TICKERED: readonly string[] = ['assetClass', 'ticker', 'name', ...TAIL];

/**
 * ADDRESS BEFORE NAME, for exactly the reason ticker precedes name (F7). The address
 * feeds the name: line 1 auto-fills it, and the fill is suppressed the moment the user
 * touches Name, so meeting a required-looking Name first defeats the feature. A property
 * is also the one asset a family identifies BY its address, so leading with it matches
 * how they already think about it.
 *
 * The address is shown whatever the valuation mode — the old form reached it only
 * through "Automated estimate", which is an unrelated decision about pricing (F5).
 */
const ORDER_REAL_ESTATE: readonly FieldOrderEntry[] = [
  // Outside every section: the class picker is the form's mode switch, not a fact about
  // the property, and it is what decides which sections exist below it.
  'assetClass',
  { section: 'Address' },
  ...ASSET_ADDRESS_FIELD_NAMES,
  // NAME OPENS THE DETAILS, it does not close the address (steward-app#963). It is what
  // the family CALLS the place rather than a part of where the place is, and its own
  // help line already explains that the street address above filled it in, so the link
  // survives the rule between them. A section of its own would be a heading over one
  // field, which is chrome, not structure.
  {
    section: 'About the property',
    // Says out loud what the fill already does, which is the half of the feature a
    // family cannot see. Honest about its limits on both sides: BLANKS only (a value
    // the family typed is never overwritten), public records rather than an appraisal,
    // and the family's own correction is the authority.
    note: 'When you pick an address above, we fill in the blanks here from public records. Look them over and correct anything we got wrong.',
  },
  'name',
  // Between Name and how it is valued, because that is the order the questions make
  // sense in: where it is, what you call it, what kind of place it is, how we value it.
  // It also sits immediately above the field that consumes it.
  ...ASSET_PROPERTY_DETAIL_FIELD_NAMES,
  // "Valuation" and not "Value": the add form ends with its own "Starting value
  // (optional)" block, two sections further down and separated by Ownership, and two
  // headings a reader would both gloss as "the value" is worse than none. This one is
  // about METHOD, that one is about a number. It also avoids restating the field's own
  // label ("How this property is valued") as the heading directly above it.
  //
  // The one heading here over a single field, which `name` was denied. It earns it:
  // this is the boundary between describing the property and deciding how Steward
  // treats it, the field is full width and carries its own explanatory paragraph, so
  // it does not read as a stranded input, and unlike `name` it is not one attribute
  // among the attributes on either side of it.
  { section: 'Valuation' },
  'valuationMode',
  // "Ownership" and not "Account and ownership": `lockAccount` omits the account field
  // on a form opened from inside an account, and a heading is not allowed to name a
  // field that may not be there.
  { section: 'Ownership' },
  ...TAIL,
];

// ── The registry ──────────────────────────────────────────────────────────────

export interface AssetClassSpec {
  /** Stable stored value. Never renamed; never shown to a user. */
  readonly code: string;
  /** What a family reads. Safe to change without touching stored data. */
  readonly label: string;
  /**
   * Other stored spellings that mean this class, lowercase. The code and the label
   * are matched automatically and need not be repeated here.
   */
  readonly synonyms: readonly string[];
  readonly group: AssetGroup;
  /** A market symbol is meaningful, so the form offers a ticker typeahead. */
  readonly tickered: boolean;
  /**
   * Value is refreshed from a daily price feed rather than carried at its last
   * appraisal. Narrower than `tickered`: a mutual fund has a symbol worth recording
   * but is NAV-priced, and no configured source can quote one.
   */
  readonly marketPriced: boolean;
  /** Real property, so it carries an address and a valuation mode. */
  readonly realEstate: boolean;
  /** Example that keeps the single Name field self-explanatory without help text. */
  readonly namePlaceholder?: string;
  /**
   * Ordered attribute names the add/edit forms render, optionally interleaved with
   * `{ section }` headings. See the note on F7 above, and on SECTIONS below.
   */
  readonly fieldOrder: readonly FieldOrderEntry[];
  /**
   * Per-class tweaks to a shared field's spec, merged over it by `assetFormFields`.
   * Lets one class present a common field differently without forking the field.
   */
  readonly fieldOverrides?: Readonly<Record<string, Partial<AssetFieldSpec>>>;
}

/**
 * On a tickered class the Name is the LOOKUP'S OUTPUT, so it stops presenting as an
 * upfront demand (F7). Submit still refuses an empty name; what changes is that the
 * field reads as pre-filled and editable rather than as the first thing to type — which
 * is precisely the misreading that made users suppress the auto-fill.
 */
const TICKERED_OVERRIDES: Readonly<Record<string, Partial<AssetFieldSpec>>> = {
  name: { required: false, help: "Filled in from the ticker. Edit it if you'd like a different label." },
};

/**
 * The same treatment for a property, where the street address fills the Name. Without
 * the help line the auto-filled "967 Shire Ct" reads as the app naming the family's
 * house for them; with it, the street address is visibly just a starting point.
 */
const REAL_ESTATE_OVERRIDES: Readonly<Record<string, Partial<AssetFieldSpec>>> = {
  name: {
    required: false,
    help: 'Filled in from the address. Change it to whatever your family calls it, like The Lake House.',
    // FULL WIDTH, and the details grid below depends on it (steward-app#963). Name is
    // the first field of "About the property" and the section has an odd number of
    // fields after it, so a half-width Name shunts every later pair one cell over and
    // splits `lastSalePrice` from `lastSaleDate` across two rows. Those two are one
    // fact, and reading a price with no date beside it is precisely the misreading the
    // field's own help text exists to prevent. Full width also stops the family's name
    // for their home from sharing a row with "Country".
    fullWidth: true,
  },
};

/**
 * Every class Steward offers, in picker order. `code` values match what
 * `plaidMappers.mapAssetClass` emits for synced holdings, so a manually added stock
 * and a synced one store the same string.
 */
export const ASSET_CLASSES: readonly AssetClassSpec[] = [
  {
    // Renamed from "Equity" (F19): "Equity" collided with "Private Equity" in the
    // same list, and it is jargon in a product that otherwise speaks plainly. The
    // stored code stays `equity` forever, so this was a label edit, not a migration.
    code: 'equity', label: 'Stock', synonyms: ['stock', 'stocks', 'public equity'],
    group: 'Equities', tickered: true, marketPriced: true, realEstate: false,
    namePlaceholder: 'e.g. Apple Inc.', fieldOrder: ORDER_TICKERED, fieldOverrides: TICKERED_OVERRIDES,
  },
  {
    // NOT folded into Stock (F19), despite both carrying tickers. A portfolio of
    // individual names carries concentration risk a diversified fund does not, and
    // the rollup that makes them comparable already exists one layer up: both report
    // under the `Equities` group.
    code: 'etf', label: 'ETF', synonyms: ['exchange traded fund'],
    group: 'Equities', tickered: true, marketPriced: true, realEstate: false,
    namePlaceholder: 'e.g. Vanguard Total Stock Market ETF', fieldOrder: ORDER_TICKERED, fieldOverrides: TICKERED_OVERRIDES,
  },
  {
    // Tickered but NOT market-priced: a fund carries a symbol (VTSAX) worth capturing,
    // yet is NAV-priced rather than exchange-quoted, so no configured source can quote
    // it. The field records what the holding IS; it does not promise a price.
    code: 'mutual fund', label: 'Mutual Fund', synonyms: ['fund'],
    group: 'Equities', tickered: true, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Vanguard Total Stock Market Index', fieldOrder: ORDER_TICKERED, fieldOverrides: TICKERED_OVERRIDES,
  },
  {
    code: 'options', label: 'Options', synonyms: ['derivative', 'option'],
    group: 'Options', tickered: true, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. AAPL Jan 2027 $200 calls', fieldOrder: ORDER_TICKERED, fieldOverrides: TICKERED_OVERRIDES,
  },
  {
    code: 'fixed income', label: 'Fixed Income', synonyms: ['bond', 'bonds'],
    group: 'Fixed Income', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. US Treasury 10-year', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'real estate', label: 'Real Estate', synonyms: ['realestate', 'real_estate', 'real-estate', 'property'],
    group: 'Real Estate', tickered: false, marketPriced: false, realEstate: true,
    namePlaceholder: 'e.g. Lake house', fieldOrder: ORDER_REAL_ESTATE,
    fieldOverrides: REAL_ESTATE_OVERRIDES,
  },
  {
    code: 'cash', label: 'Cash', synonyms: ['money market'],
    group: 'Cash', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Operating cash', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'private equity', label: 'Private Equity', synonyms: [],
    group: 'Private Equity', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Acme Ventures Fund II', fieldOrder: ORDER_PLAIN,
  },
  {
    // steward-app#1027 (RIPL, via docs/31): "business valuation / equity-in-business
    // category". NOT folded into `private equity` (F19-style split, same reasoning as
    // ETF vs Stock): a family's own operating company and an LP stake in someone else's
    // fund are both illiquid ownership a family holds directly, but they are not the same
    // thing to hold or to talk about.
    //
    // #1027 shipped this class under the `Private Equity` GROUP, so the dashboard's
    // allocation bar and Portfolio's grouping labelled a family's hardware store "Private
    // Equity", which is the surface RIPL's ask was actually aimed at. steward-app#1195
    // gave it the `Business` group of its own (see `ASSET_GROUP_ORDER`). That regroup
    // moved no stored figure: `computeSnapshot` looks up only `Equities`, `Real Estate`
    // and `Cash` by group name and derives `otherAssetsValue` as the residual, so a
    // business valuation lands in `otherAssetsValue` exactly once under either group.
    // The calculator test for #1027 asserts those totals, not the group.
    //
    // `'private business'` and `'business'` move here from `private equity`'s synonyms,
    // which is what they always meant — the two-pass alias build below was written
    // anticipating exactly this class landing (see its own comment).
    //
    // Valued the same way every non-market-priced class is: a hand-entered `Valuation`
    // row (date, marketValue, source, notes) via the existing `valuations.ts` create/update
    // handlers. No new write path. `assetLots` is irrelevant here (`ORDER_PLAIN`, no
    // ticker), and `ownerEntityId` — already a field on every class — is how this hangs
    // off the shipped entities/ownership pillar (an LLC or trust that owns the business)
    // rather than duplicating it: `Entity` itself carries no value field and
    // `computeSnapshot` never reads entities at all, so there is no path for a business's
    // value to be counted through both the asset and its owning entity.
    code: 'business', label: 'Business',
    synonyms: ['private business', 'owned business', 'family business', 'operating business'],
    group: 'Business', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Anderson Family Hardware', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'precious metals', label: 'Precious Metals',
    synonyms: ['precious metal', 'metals', 'gold', 'silver', 'bullion'],
    group: 'Precious Metals', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Gold bullion', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'art & collectibles', label: 'Art & Collectibles',
    synonyms: ['art', 'collectible', 'collectibles', 'watches', 'wine'],
    group: 'Collectibles', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Untitled, 1963', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'jewelry', label: 'Jewelry', synonyms: ['jewellery'],
    group: 'Collectibles', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Cartier bracelet', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'vehicle', label: 'Vehicle',
    synonyms: ['vehicles', 'auto', 'automobile', 'car', 'boat', 'aircraft'],
    group: 'Vehicles', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. 2022 Land Cruiser', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'life insurance', label: 'Life Insurance',
    synonyms: ['insurance', 'whole life', 'universal life'],
    group: 'Insurance', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Whole life policy, Northwestern', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'loan receivable', label: 'Loan Receivable',
    synonyms: ['loans receivable', 'note receivable', 'notes receivable', 'receivable', 'loan'],
    group: 'Loans Receivable', tickered: false, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Note receivable, Smith family', fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'alternative', label: 'Alternative', synonyms: [],
    group: 'Alternative', tickered: false, marketPriced: false, realEstate: false,
    fieldOrder: ORDER_PLAIN,
  },
  {
    code: 'crypto', label: 'Crypto', synonyms: ['cryptocurrency'],
    group: 'Crypto', tickered: true, marketPriced: false, realEstate: false,
    namePlaceholder: 'e.g. Bitcoin', fieldOrder: ORDER_TICKERED, fieldOverrides: TICKERED_OVERRIDES,
  },
  {
    code: 'other', label: 'Other', synonyms: [],
    group: 'Other', tickered: false, marketPriced: false, realEstate: false,
    fieldOrder: ORDER_PLAIN,
  },
];

/**
 * Every stored spelling we recognise → its spec. Built once, matched lowercase.
 *
 * TWO PASSES, and the order is the whole point: a class's own `code` and `label` are
 * registered for every class BEFORE any synonym is. Otherwise registry order would
 * decide, and an earlier class's synonym would swallow a later class's own code —
 * `'insurance'` (a synonym of `life insurance`) would win over a future class declaring
 * that code, silently and without any error. This is not hypothetical: `'business'` used
 * to be a synonym of `private equity`, and when the `business` class landed
 * (steward-app#1027) the two-pass build is what let that synonym move to the new class's
 * own declaration without a silent shadowing bug.
 * With codes and labels claimed first, a synonym can only ever fill a gap.
 * `src/lib/assetClass.test.ts` (the SPA re-export's own test) asserts every alias
 * resolves back to the spec that declared it — there is no `assetClasses.test.ts` in
 * this package; that stale pointer used to name a file that never existed here.
 */
const SPEC_BY_ALIAS: ReadonlyMap<string, AssetClassSpec> = (() => {
  const map = new Map<string, AssetClassSpec>();
  const claim = (alias: string, spec: AssetClassSpec) => {
    const key = alias.trim().toLowerCase();
    if (!map.has(key)) map.set(key, spec);
  };
  for (const spec of ASSET_CLASSES) { claim(spec.code, spec); claim(spec.label, spec); }
  for (const spec of ASSET_CLASSES) for (const s of spec.synonyms) claim(s, spec);
  return map;
})();

// ── Resolution ────────────────────────────────────────────────────────────────

/**
 * The spec for a STORED class value, matched case-insensitively against codes,
 * labels and synonyms, or `undefined` when nothing matches.
 *
 * Returning `undefined` rather than a fallback is deliberate: callers that display a
 * class must keep showing the user's own spelling, and callers that decide behaviour
 * must be able to tell "unknown" from "Other".
 */
export function resolveAssetClass(stored: string | undefined): AssetClassSpec | undefined {
  if (!stored) return undefined;
  return SPEC_BY_ALIAS.get(stored.trim().toLowerCase());
}

/** The canonical stored code for a class value; falls back to the value itself. */
export function assetClassCode(stored: string): string {
  return resolveAssetClass(stored)?.code ?? stored;
}

/** What a family reads for a class value; unrecognised values keep their spelling. */
export function assetClassLabel(stored: string): string {
  return resolveAssetClass(stored)?.label ?? stored;
}

/** The reporting rollup for a class value. Unrecognised classes report under Other. */
export function assetGroupFor(stored: string): AssetGroup {
  return resolveAssetClass(stored)?.group ?? 'Other';
}

/** True when a market symbol is meaningful for the class. */
export function isTickeredClass(stored: string): boolean {
  return resolveAssetClass(stored)?.tickered ?? false;
}

/**
 * True when the class is valued from a daily price feed rather than from its last
 * recorded appraisal.
 */
export function isMarketPricedClass(stored: string): boolean {
  return resolveAssetClass(stored)?.marketPriced ?? false;
}

/** True when the class is real property (carries an address and a valuation mode). */
export function isRealEstateClass(stored: string): boolean {
  return resolveAssetClass(stored)?.realEstate ?? false;
}

/** The stored codes of every market-priced class, for a price source's capability list. */
export const MARKET_PRICED_CODES: readonly string[] =
  ASSET_CLASSES.filter(c => c.marketPriced).map(c => c.code);

/**
 * Reporting groups whose holdings are held AWAY FROM ANY brokerage custodian, by
 * construction — not because of who the family banks with, but because of what the group
 * IS. No custodian (Altruist included) opens an account that holds a house, a car, or a
 * stake in a family's own business; those assets exist outside the custodial system
 * entirely, however they were entered into Steward (steward-app#1268's disposition of the
 * advisor dashboard ask, docs/31 line 164).
 *
 * ⚠️ **This is the WHOLE of what can be classified WITHOUT an institution match.** `Cash`
 * and `Fixed Income` are deliberately absent: a checking account or a bond COULD be held
 * at a firm's custodian (several offer cash management), so telling those two apart from
 * an off-custodian account of the same kind needs an institution match — see
 * {@link LIQUID_GROUPS} and `services/snapshot-calculator`'s `isHeldAtCustodian` for that
 * half, which the nightly snapshot job now computes (steward-app#1268's on-platform half).
 * Adding either group here instead would silently claim "held away" for a family whose
 * cash genuinely sits at their advisor's custodian, which is exactly the wrong-number
 * failure docs/26 §2a exists to prevent. `Equities`/`Options`/`Crypto` are absent for the
 * same reason in the other direction: they typically CAN sit at a brokerage custodian, so
 * an institution match would be needed before either total may claim them — none is
 * computed for those groups today, so a holding in one is simply uncounted by either half
 * of the split, not misclassified.
 *
 * `Insurance` and `Loans Receivable` are also absent, deliberately left uncounted rather
 * than guessed: a life-insurance cash value or money owed to the family is not clearly
 * either bucket, and this set exists to be conservative, not exhaustive — omission here
 * costs a display line, never a wrong figure.
 */
export const NEVER_CUSTODIED_GROUPS: ReadonlySet<AssetGroup> = new Set<AssetGroup>([
  'Real Estate', 'Business', 'Private Equity', 'Vehicles', 'Collectibles',
  'Precious Metals', 'Alternative',
]);

/** True for a reporting group no brokerage custodian can hold, per {@link NEVER_CUSTODIED_GROUPS}. */
export function isNeverCustodiedGroup(group: AssetGroup): boolean {
  return NEVER_CUSTODIED_GROUPS.has(group);
}

// Moved here unchanged from the façade's `handlers/advisor.ts` (steward-app#2259), docblock
// included, so that `latestSnapshotFor` there and `heldFromSnapshot` in
// `firmNetWorthSeries.ts` derive the non-liquid figure with one function. The docblock was
// written in that file: its "this reader" and "`currencyCode` above" are the façade's.
/**
 * Roll a stored `net-worth-snapshot.assetBreakdown` up to the off-platform, non-liquid
 * figure `AdvisorSummaryClient.offPlatformNonLiquidValue` carries (steward-app#1268's
 * non-liquid half, docs/31 line 164).
 *
 * `assetBreakdown` is schemaless and keyed by the asset's STORED class code
 * (`services/snapshot-calculator/src/calculator.ts`), so this reads it exactly as
 * defensively as that writer's own docblock treats a caller-supplied key: `null` for
 * anything that is not a plain object (absent — predates this field; an array; `null`
 * itself), and each entry's value is required to be a finite number before it contributes,
 * the same guard `latestSnapshotFor` already applies to the top-level `netWorth`. One
 * malformed entry does not spoil the rest — it is simply excluded, on the same reasoning
 * `IntakeStatements.tsx` gives for one bad attachment not taking a submission's others down.
 *
 * `assetGroupFor`/`isNeverCustodiedGroup` are the exact pair `computeSnapshot` uses to
 * roll the SAME breakdown into `equitiesValue`/`realEstateValue`/`cashValue` at write time,
 * so this reader cannot classify a class code differently than the snapshot that produced
 * it — the identical argument `currencyCode` above is imported for rather than re-derived.
 */
export function offPlatformNonLiquidFromBreakdown(raw: unknown): number | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  let total = 0;
  for (const [code, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    if (isNeverCustodiedGroup(assetGroupFor(code))) total += value;
  }
  return total;
}

/**
 * Reporting groups that are "cash-style" in RIPL's own words (Asana task 1217954392116147,
 * 2026-08-28): *"any cash sitting in accounts checking or savings or money market or
 * bonds etc — 'cash' style positions not in a position to get market returns."* That is
 * `Cash` and `Fixed Income` — deliberately NOT `isMarketPricedClass`'s notion of
 * market-priced, which is a pricing-mechanism distinction (priced from a daily feed vs.
 * carried at appraisal) and answers a different question than "does this grow with the
 * market."
 *
 * Consumed by `services/snapshot-calculator`'s custody split: a holding in one of these
 * groups that is NOT at the firm's custodian is the "held away, liquid" figure. A holding
 * in neither this set nor {@link NEVER_CUSTODIED_GROUPS} (equities, options, crypto, and
 * the deliberately-uncounted pair that set's own doc names) contributes to neither
 * off-platform bucket — see that split's own doc for why the two are not a partition of
 * everything held away.
 */
export const LIQUID_GROUPS: ReadonlySet<AssetGroup> = new Set<AssetGroup>(['Cash', 'Fixed Income']);

/** True for a "cash-style" reporting group, per {@link LIQUID_GROUPS}. */
export function isLiquidGroup(group: AssetGroup): boolean {
  return LIQUID_GROUPS.has(group);
}

/** The name example for a class's Name field, or a neutral default. */
export function assetNamePlaceholder(stored: string): string {
  return resolveAssetClass(stored)?.namePlaceholder ?? 'e.g. Apple Inc. or Lake house';
}

/**
 * The ordered form fields for a class. An unrecognised class gets the plain order,
 * so an asset synced under a class we do not know is still fully editable.
 */
export function assetFormFields(stored: string): readonly AssetFieldSpec[] {
  return assetFormSections(stored).flatMap(g => g.fields);
}

/**
 * The same fields, grouped by the `{ section }` markers in the class's order.
 *
 * A class with no markers answers with ONE untitled group holding every field, which
 * is byte-for-byte the old flat render — so classes that never needed headings did not
 * silently acquire them, and `assetFormFields` above stays the flattening of this.
 *
 * A heading with no fields under it is dropped rather than rendered empty: a marker
 * followed only by names this class does not declare (or that the caller omits, e.g.
 * `accountId` on a locked-account form) would otherwise leave a heading floating over
 * nothing. `AssetFields` re-applies the same rule after ITS `omit` filter, because the
 * registry cannot see that list.
 */
export function assetFormSections(stored: string): readonly AssetFieldGroup[] {
  const spec = resolveAssetClass(stored);
  const overrides = spec?.fieldOverrides;
  const groups: AssetFieldGroup[] = [];
  let current: { title?: string; note?: string; fields: AssetFieldSpec[] } = { fields: [] };

  for (const entry of spec?.fieldOrder ?? ORDER_PLAIN) {
    if (isSectionMarker(entry)) {
      if (current.fields.length) groups.push(current);
      current = { title: entry.section, note: entry.note, fields: [] };
      continue;
    }
    const field = FIELD_BY_NAME.get(entry);
    if (!field) continue;
    current.fields.push(overrides?.[field.name] ? { ...field, ...overrides[field.name] } : field);
  }
  if (current.fields.length) groups.push(current);
  return groups;
}

/**
 * Options for an asset-class picker: every canonical class, plus the stored value
 * itself when it matches nothing, so an unrecognised class stays visible and
 * selectable rather than being silently swapped for the first entry.
 */
export function assetClassOptions(stored?: string): Array<{ value: string; label: string }> {
  const options = ASSET_CLASSES.map(c => ({ value: c.code, label: c.label }));
  if (stored && !resolveAssetClass(stored)) options.push({ value: stored, label: stored });
  return options;
}

/**
 * The value an asset-class `<select>` should carry for a stored class. A `<select>`
 * matches its options by EXACT string, so handing it the raw stored value would
 * render the FIRST option as selected for anything not spelled exactly like a code
 * (`'Equity'`, or Plaid's `'etf'` before this registry existed) — and then SAVE that
 * wrong class on the next submit.
 */
export function assetClassSelectValue(stored: string): string {
  return resolveAssetClass(stored)?.code ?? stored;
}

/**
 * Options for a registry-declared `select`, carrying the same guard `assetClassOptions`
 * carries and for the same reason: a `<select>` matches by EXACT string, so a stored
 * value absent from the list renders the FIRST option as selected and then SAVES that
 * wrong value on the next submit. Silently rewriting a townhouse to a detached house is
 * precisely the failure this field was added to fix, so it must not be reintroduced by
 * the picker itself.
 *
 * An optional field also needs an explicit empty choice. Without one the first real
 * option shows as selected while the stored value is blank, which asserts a property
 * type the family never chose.
 */
export function fieldSelectOptions(
  spec: AssetFieldSpec,
  stored?: string,
): readonly AssetFieldOption[] {
  const declared = spec.options ?? [];
  const options: AssetFieldOption[] = [
    { value: '', label: spec.required ? 'Select one' : 'Not specified' },
    ...declared,
  ];
  if (stored && !declared.some(o => o.value === stored)) {
    options.push({ value: stored, label: stored });
  }
  return options;
}

// ── Addresses ─────────────────────────────────────────────────────────────────

/**
 * Composes the structured parts into the single line an AVM provider expects
 * ("Street, City, State Zip"). The parts are what Steward stores and shows; a
 * formatted string is produced only at the point a provider is called, so the
 * structure is never lost to a round trip through free text.
 *
 * Returns `''` when there is nothing to format, which callers treat as "no address".
 */
export function formatAddress(a: StructuredAddress): string {
  const stateZip = [a.addressState, a.addressPostalCode]
    .map(p => p?.trim())
    .filter(Boolean)
    .join(' ');
  // US is the implied default, so naming it adds noise without adding precision.
  // A non-US country is kept, since it is information.
  const keepCountry = isUsAddress(a) ? undefined : a.addressCountry?.trim();
  return [
    a.addressLine1?.trim(),
    a.addressLine2?.trim(),
    a.addressCity?.trim(),
    stateZip || undefined,
    keepCountry,
  ].filter(Boolean).join(', ');
}

const US_COUNTRY_NAMES = ['us', 'usa', 'united states', 'united states of america'];

/**
 * True when the property is in the United States, treating a blank country as US
 * because that is the implied default here.
 *
 * Stated once because two callers need the same rule, for related but distinct reasons:
 * `formatAddress` drops a redundant "United States" from the composed line, and the
 * property form only defaults to automatic estimates where our valuation provider
 * actually has coverage. Deliberately a DEFAULTING signal, never a block: someone may
 * know better than this heuristic, and a family with a flat in London can still choose
 * automatic and find out.
 */
export function isUsAddress(a: StructuredAddress): boolean {
  const country = a.addressCountry?.trim().toLowerCase();
  return !country || US_COUNTRY_NAMES.includes(country);
}

/** True when enough of the address is present to be worth showing or sending. */
export function hasAddress(a: StructuredAddress): boolean {
  return ASSET_ADDRESS_FIELD_NAMES.some(
    name => (a as Record<string, string | undefined>)[name]?.trim(),
  );
}

/**
 * The address parts that must be filled in for an automated valuation, in form
 * order. Derived from the field specs so the rule is stated once: a field is
 * AVM-required exactly when its spec says so.
 */
export const AVM_REQUIRED_ADDRESS_FIELDS: readonly AssetFieldSpec[] =
  ASSET_ADDRESS_FIELDS.filter(
    f => f.requiredWhen?.field === 'valuationMode' && f.requiredWhen.equals === 'avm',
  );

/**
 * The AVM-required address parts that are missing from `a`, as their field specs.
 *
 * The projection callers want differs: everyone who states the rule wants the labels
 * (below), while a form that has to TAKE THE USER TO the offending input wants the
 * field names. Splitting the two here keeps that one rule — which parts count as
 * missing — stated once, rather than having the SPA re-derive it from
 * `AVM_REQUIRED_ADDRESS_FIELDS` and drift.
 */
export function missingAvmAddressFieldSpecs(a: StructuredAddress): AssetFieldSpec[] {
  const bag = a as Record<string, string | undefined>;
  return AVM_REQUIRED_ADDRESS_FIELDS.filter(f => !bag[f.name]?.trim());
}

/**
 * The AVM-required address parts that are missing from `a`, by label. Empty means
 * the address is complete enough to send. Shared by the SPA (which blocks submit)
 * and the façade (which rejects the write), so the two cannot disagree about what
 * "has an address" means.
 */
export function missingAvmAddressFields(a: StructuredAddress): string[] {
  return missingAvmAddressFieldSpecs(a).map(f => f.label);
}
